/* =========================================================================
 * A NEW CHAMPIONSHIP OPPORTUNITY — pure composition, no React, no I/O.
 *
 * Brian: "we should easily be able to add new championship opportunities."
 *
 * The 2026 USEF Para Dressage hunt is why. A governing body SR was not tracking
 * ran a national championship with two crowns in it, and there was no way to put
 * it into the universe — the board can only ever work the records it already
 * has, so a championship nobody had entered was invisible no matter how good the
 * hunting got.
 *
 * `POST /api/championships/load` already creates them and is idempotent on
 * `sr_championship_key` — an existing key is SKIPPED, never duplicated. So the
 * whole job here is composing a good key and a clean property set.
 *
 * ⚠️ THE KEY IS THE DEDUPE, so it is derived, never typed. Two people adding
 * "NCHSAA 4A Football 2026" a week apart must produce the same key or the
 * universe grows a duplicate that every later count has to reconcile. Same
 * shape the repair tooling already uses: org|name|sport|gender|year.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()

export function slug(v) {
  return S(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

/**
 * The dedupe key. Derived from the identifying fields so the same championship
 * entered twice collides instead of duplicating.
 *
 * Gender is in the key because a men's and a women's championship of the same
 * name in the same year are two different championships — leaving it out is how
 * you silently merge them.
 */
export function championshipKey({ org, name, sport, gender, year } = {}) {
  const parts = [slug(org) || 'sr', slug(name), slug(sport), slug(gender) || 'all', S(year)]
  if (!parts[1] || !parts[4]) return ''
  return parts.join('|')
}

/**
 * What a new championship must have before it is worth creating.
 *
 * Deliberately SHORT. This is an opportunity being added to the board, not a
 * finished record — the hunt and the resolve layer fill the rest in. Demanding
 * a full record up front is how a "quick add" becomes something nobody uses.
 */
export function checkNewChampionship(f = {}) {
  const out = []
  if (!S(f.name)) out.push('name is required — this is what a rep will read on the board')
  if (!S(f.seasonYear)) out.push('season year is required — it is half of the dedupe key')
  else if (!/^\d{4}$/.test(S(f.seasonYear))) out.push('season year must be a 4-digit year')
  if (!S(f.sport)) out.push('sport is required — it drives colour, filtering and the hunt')
  /* A DATE IN THE FUTURE IS FINE HERE and that is the point: this is an
   * OPPORTUNITY, something to pursue before it happens. The crown is what may
   * never carry a future date. */
  if (S(f.eventDate) && !/^\d{4}-\d{2}-\d{2}$/.test(S(f.eventDate))) {
    out.push('event date must be YYYY-MM-DD')
  }
  if (!championshipKey({ org: f.org, name: f.name, sport: f.sport, gender: f.gender, year: f.seasonYear })) {
    out.push('could not build a dedupe key from these fields')
  }
  return out
}

/**
 * Compose the HubSpot property set. Only fields the operator actually filled —
 * an empty string written to a property is not the same as leaving it unset,
 * and `createChampionship` drops empties anyway, so this keeps the payload
 * honest about what was really provided.
 */
export function toChampionshipItem(f = {}) {
  const key = championshipKey({ org: f.org, name: f.name, sport: f.sport, gender: f.gender, year: f.seasonYear })
  const item = {
    sr_championship_key: key,
    championship_name: S(f.name),
    sr_sport: S(f.sport),
    sr_season_year: S(f.seasonYear),
  }
  const opt = {
    sr_level: S(f.level),
    sr_gender: S(f.gender),
    sr_state: S(f.state),
    sr_conference: S(f.org),
    sr_segment: S(f.segment),
    sr_event_date: S(f.eventDate),
    /* A hand-entered date is DATED, not Confirmed. Confirmed is the status a
     * person is most likely to trust, and it should mean somebody verified it
     * against the governing body — not that somebody typed it into a form. */
    sr_date_status: S(f.eventDate) ? 'Dated' : 'PENDING',
    sr_pursuit_status: 'To Pursue',
  }
  for (const [k, v] of Object.entries(opt)) if (v) item[k] = v
  return item
}

/** One line describing what will happen, for the confirm step. */
export function describeCreate(f = {}) {
  const item = toChampionshipItem(f)
  return {
    key: item.sr_championship_key,
    name: item.championship_name,
    fields: Object.keys(item).length,
    note: 'If this key already exists it will be SKIPPED, not duplicated.',
  }
}
